import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@/hooks/useRedux';
import { getMeAsync } from '@/store/authSlice';
import { MainLayout, POSLayout } from '@/components/layout';

// ── Lazy pages ────────────────────────────────────────────────
const LoginPage       = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage    = lazy(() => import('@/features/auth/pages/RegisterPage'));
const OnboardingPage  = lazy(() => import('@/features/onboarding/pages/OnboardingPage'));
const ConfirmarEmailPage = lazy(() => import('@/features/auth/pages/ConfirmarEmailPage'));
const DashboardPage   = lazy(() => import('@/features/auth/pages/DashboardPage'));
const POSTerminal     = lazy(() => import('@/features/pos/pages/POSTerminal'));
const ProductosPage   = lazy(() => import('@/features/catalogo/pages/ProductosPage'));
const ProductoFormPage= lazy(() => import('@/features/catalogo/pages/ProductoFormPage'));
const CategoriasPage      = lazy(() => import('@/features/catalogo/pages/CategoriasPage'));
const ProductosCampoPage  = lazy(() => import('@/features/catalogo/pages/ProductosCampoPage'));
const ProveedoresPage = lazy(() => import('@/features/catalogo/pages/ProveedoresPage'));
const StockPage       = lazy(() => import('@/features/inventario/pages/StockPage'));
const MovimientosPage = lazy(() => import('@/features/inventario/pages/MovimientosPage'));
const TransferenciasPage = lazy(() => import('@/features/inventario/pages/TransferenciasPage'));
const ConteosPage     = lazy(() => import('@/features/inventario/pages/ConteosPage'));
const VentasHistorialPage = lazy(() => import('@/features/ventas/pages/VentasHistorialPage'));
const GestionStockPage = lazy(() => import('@/features/inventario/pages/GestionStockPage'));
const LotesPage       = lazy(() => import('@/features/inventario/pages/LotesPage'));
const PromocionesPage = lazy(() => import('@/features/promociones/pages/PromocionesPage'));
const PromocionFormPage = lazy(() => import('@/features/promociones/pages/PromocionFormPage'));
const CampanasPage    = lazy(() => import('@/features/promociones/pages/CampanasPage'));
const CampanaFormPage = lazy(() => import('@/features/promociones/pages/CampanaFormPage'));
const ClientesPage    = lazy(() => import('@/features/clientes/pages/ClientesPage'));
const ClienteFormPage = lazy(() => import('@/features/clientes/pages/ClienteFormPage'));
const UsuariosPage      = lazy(() => import('@/features/configuracion/pages/UsuariosPage'));
const EmpresaPage       = lazy(() => import('@/features/configuracion/pages/EmpresaPage'));
const SucursalesConfigPage  = lazy(() => import('@/features/configuracion/pages/SucursalesPage'));
const AsignacionUsuariosPage = lazy(() => import('@/features/configuracion/pages/AsignacionUsuariosPage'));
const PerfilesPOSPage          = lazy(() => import('@/features/configuracion/pages/PerfilesPOSPage'));
const AdministracionPage       = lazy(() => import('@/features/configuracion/pages/AdministracionPage'));
const GestionCajaPage    = lazy(() => import('@/features/caja/pages/GestionCajaPage'));
const FacturacionDIANPage = lazy(() => import('@/features/facturacion/pages/FacturacionDIANPage'));
const PerifericosPage     = lazy(() => import('@/features/configuracion/pages/PerifericosPage'));
const MetodosPagoPage     = lazy(() => import('@/features/configuracion/pages/MetodosPagoPage'));
const AlmacenCatalogoPage      = lazy(() => import('@/features/configuracion/pages/AlmacenCatalogoPage'));
const ImportacionPage        = lazy(() => import('@/features/importacion/pages/ImportacionPage'));
const ListasPreciosPage      = lazy(() => import('@/features/precios/pages/ListasPreciosPage'));

// ── Restaurante / comidas rápidas ─────────────────────────────
const TipoNegocioPage    = lazy(() => import('@/features/restaurante/pages/TipoNegocioPage'));
const ModificadoresPage  = lazy(() => import('@/features/restaurante/pages/ModificadoresPage'));
const RecetasPage        = lazy(() => import('@/features/restaurante/pages/RecetasPage'));
const MesasConfigPage    = lazy(() => import('@/features/restaurante/pages/MesasConfigPage'));
const CocinaPage         = lazy(() => import('@/features/restaurante/pages/CocinaPage'));
const TurnosPage         = lazy(() => import('@/features/restaurante/pages/TurnosPage'));
const MenuVirtualPage    = lazy(() => import('@/features/restaurante/pages/MenuVirtualPage'));
const MenuPublicoPage    = lazy(() => import('@/features/menu/pages/MenuPublicoPage'));

// ── Crédito ───────────────────────────────────────────────────
const CreditoDashboardPage      = lazy(() => import('@/features/credito/pages/CreditoDashboardPage'));
const SolicitudesCreditoPage    = lazy(() => import('@/features/credito/pages/SolicitudesCreditoPage'));
const GestionCobroPage          = lazy(() => import('@/features/credito/pages/GestionCobroPage'));
const ComunicacionesCreditoPage = lazy(() => import('@/features/credito/pages/ComunicacionesCreditoPage'));
const CreditoConfiguracionPage  = lazy(() => import('@/features/credito/pages/CreditoConfiguracionPage'));
const ClientesCreditoPage       = lazy(() => import('@/features/credito/pages/ClientesCreditoPage'));
const ClienteCreditoPage        = lazy(() => import('@/features/credito/pages/ClienteCreditoPage'));

// ── Domicilios ────────────────────────────────────────────────
const DespachoDomiciliosPage    = lazy(() => import('@/features/domicilios/pages/DespachoDomiciliosPage'));
const DomiciliariosPage         = lazy(() => import('@/features/domicilios/pages/DomiciliariosPage'));
const DomiciliarioApp           = lazy(() => import('@/features/domicilios/pages/DomiciliarioApp'));

// ── Fallbacks ─────────────────────────────────────────────────
const Spinner = () => (
  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
    <CircularProgress />
  </Box>
);
const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Spinner />}>{children}</Suspense>
);

// ── Guards con Redux real ─────────────────────────────────────
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, token, requiereOnboarding, user, isLoading } = useAppSelector((s) => s.auth);
  const dispatch = useAppDispatch();
  // Entrada directa (recarga) a /pos, /cocina o /turnos: esas pantallas no
  // usan MainLayout, así que el usuario se carga aquí.
  useEffect(() => {
    if (token && !user && !isLoading) dispatch(getMeAsync());
  }, [token, user, isLoading, dispatch]);
  if (!isAuthenticated && !token) return <Navigate to="/login" replace />;
  // Modo Ecolab: el asociado entró pero todavía no creó su empresa.
  // Sin empresa no hay sucursal, ni perfil, ni permisos: el POS no puede
  // operar. Se le manda al onboarding hasta que la cree.
  if (requiereOnboarding) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
};

/** Solo accesible mientras falte el onboarding. Si ya tiene empresa, al inicio. */
const OnboardingGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, token, requiereOnboarding } = useAppSelector((s) => s.auth);
  if (!isAuthenticated && !token) return <Navigate to="/login" replace />;
  // ?nueva=1: quien ya tiene empresa puede registrar otra.
  const nueva = new URLSearchParams(window.location.search).get('nueva') === '1';
  if (!requiereOnboarding && !nueva) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const GuestGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** Cada rol entra a su pantalla: Cocina a la cocina, Mesero al POS. */
const Inicio = () => {
  const user = useAppSelector((s) => s.auth.user);
  const rol = user?.rolBase ?? user?.perfilPOS;
  if (rol === 'Cocina') return <Navigate to="/cocina" replace />;
  if (rol === 'Mesero') return <Navigate to="/pos" replace />;
  return <S><DashboardPage /></S>;
};

// ── Placeholder ───────────────────────────────────────────────
function PlaceholderPage({ title }: { title: string }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center' }}>
      <Box sx={{ fontSize:'2rem', fontWeight:700, mb:1 }}>{title}</Box>
      <Box sx={{ color:'text.secondary' }}>Esta sección está en desarrollo</Box>
    </Box>
  );
}

// ── Router ────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    path: '/domiciliario',
    element: <S><DomiciliarioApp /></S>,
  },
  {
    // Carta digital pública (sin sesión): enlace y QR del menú virtual
    path: '/menu/:slug',
    element: <S><MenuPublicoPage /></S>,
  },
  {
    path: '/login',
    element: <GuestGuard><S><LoginPage /></S></GuestGuard>,
  },
  {
    path: '/register',
    element: <GuestGuard><S><RegisterPage /></S></GuestGuard>,
  },
  {
    path: '/confirmar-email',
    element: <S><ConfirmarEmailPage /></S>,
  },
  {
    path: '/onboarding',
    element: <OnboardingGuard><S><OnboardingPage /></S></OnboardingGuard>,
  },
  {
    path: '/pos',
    element: <AuthGuard><POSLayout /></AuthGuard>,
    children: [{ index: true, element: <S><POSTerminal /></S> }],
  },
  {
    path: '/cocina',
    element: <AuthGuard><S><CocinaPage /></S></AuthGuard>,
  },
  {
    path: '/turnos',
    element: <AuthGuard><S><TurnosPage /></S></AuthGuard>,
  },
  {
    path: '/',
    element: <AuthGuard><MainLayout /></AuthGuard>,
    children: [
      { index: true,     element: <Inicio /> },
      { path: 'dashboard',element: <S><DashboardPage /></S> },

      // Catálogo
      { path: 'catalogo', children: [
        { index: true, element: <Navigate to="productos" replace /> },
        { path: 'productos',          element: <S><ProductosPage /></S> },
        { path: 'productos/nuevo',    element: <S><ProductoFormPage /></S> },
        { path: 'productos/:id',      element: <S><ProductoFormPage /></S> },
        { path: 'productos/:id/editar',element: <S><ProductoFormPage /></S> },
        { path: 'categorias',         element: <S><CategoriasPage /></S> },
        { path: 'proveedores',        element: <S><ProveedoresPage /></S> },
        { path: 'productos-campo',    element: <S><ProductosCampoPage /></S> },
      ]},

      // Inventario
      { path: 'inventario', children: [
        { index: true, element: <Navigate to="stock" replace /> },
        { path: 'stock',          element: <S><StockPage /></S> },
        { path: 'movimientos',    element: <S><MovimientosPage /></S> },
        { path: 'transferencias', element: <S><TransferenciasPage /></S> },
        { path: 'conteos',        element: <S><ConteosPage /></S> },
        { path: 'gestion-stock',  element: <S><GestionStockPage /></S> },
        { path: 'lotes',          element: <S><LotesPage /></S> },
      ]},

      // Clientes
      { path: 'clientes', children: [
        { index: true, element: <S><ClientesPage /></S> },
        { path: 'nuevo',       element: <S><ClienteFormPage /></S> },
        { path: ':id',         element: <S><ClienteFormPage /></S> },
        { path: ':id/editar',  element: <S><ClienteFormPage /></S> },
      ]},

      // Crédito
      { path: 'credito', children: [
        { index: true,              element: <S><CreditoDashboardPage /></S> },
        { path: 'solicitudes',      element: <S><SolicitudesCreditoPage /></S> },
        { path: 'cobro',            element: <S><GestionCobroPage /></S> },
        { path: 'comunicaciones',   element: <S><ComunicacionesCreditoPage /></S> },
        { path: 'configuracion',    element: <S><CreditoConfiguracionPage /></S> },
        { path: 'clientes',         element: <S><ClientesCreditoPage /></S> },
        { path: 'clientes/:id',     element: <S><ClienteCreditoPage /></S> },
      ]},
      { path: 'domicilios', children: [
        { index: true,              element: <S><DespachoDomiciliosPage /></S> },
        { path: 'domiciliarios',    element: <S><DomiciliariosPage /></S> },
      ]},

      // Promociones
      { path: 'promociones', children: [
        { index: true, element: <S><PromocionesPage /></S> },
        { path: 'nueva',          element: <S><PromocionFormPage /></S> },
        { path: ':id',            element: <S><PromocionFormPage /></S> },
        { path: ':id/editar',     element: <S><PromocionFormPage /></S> },
        { path: 'campanas',       element: <S><CampanasPage /></S> },
        { path: 'campanas/nueva', element: <S><CampanaFormPage /></S> },
        { path: 'campanas/:id',   element: <S><CampanaFormPage /></S> },
        { path: 'campanas/:id/editar', element: <S><CampanaFormPage /></S> },
      ]},

      // Ventas
      { path: 'ventas', children: [
        { index: true, element: <Navigate to="historial" replace /> },
        { path: 'historial',    element: <S><VentasHistorialPage /></S> },
        { path: 'devoluciones', element: <PlaceholderPage title="Devoluciones" /> },
      ]},

      // Restaurante
      { path: 'restaurante', children: [
        { index: true, element: <Navigate to="mesas" replace /> },
        { path: 'mesas',         element: <S><MesasConfigPage /></S> },
        { path: 'recetas',       element: <S><RecetasPage /></S> },
        { path: 'modificadores', element: <S><ModificadoresPage /></S> },
        { path: 'menu-virtual',  element: <S><MenuVirtualPage /></S> },
      ]},

      { path: 'compras',     element: <PlaceholderPage title="Órdenes de Compra" /> },
      { path: 'importacion', element: <S><ImportacionPage /></S> },
      { path: 'caja',        element: <S><GestionCajaPage /></S> },
      { path: 'administracion', element: <S><AdministracionPage /></S> },
      { path: 'facturacion', element: <S><FacturacionDIANPage /></S> },
      { path: 'reportes',    element: <PlaceholderPage title="Reportes" /> },
      { path: 'configuracion', children: [
        { index: true, element: <Navigate to="/administracion" replace /> },
        { path: 'usuarios',      element: <Navigate to="/administracion#usuarios" replace /> },
        { path: 'general',       element: <PlaceholderPage title="Configuración General" /> },
        { path: 'empresa',       element: <S><EmpresaPage /></S> },
        { path: 'negocio',       element: <S><TipoNegocioPage /></S> },
        { path: 'sucursales',    element: <S><SucursalesConfigPage /></S> },
        { path: 'sucursales-cfg',    element: <S><SucursalesConfigPage /></S> },
        { path: 'catalogo-almacen',  element: <S><AlmacenCatalogoPage /></S> },
        { path: 'metodos-pago',      element: <S><MetodosPagoPage /></S> },
        { path: 'perifericos',       element: <S><PerifericosPage /></S> },
        { path: 'listas-precios',    element: <S><ListasPreciosPage /></S> },
        { path: 'perfiles-pos',  element: <S><PerfilesPOSPage /></S> },
        { path: 'asignaciones',  element: <S><AsignacionUsuariosPage /></S> },
        { path: 'impuestos',     element: <PlaceholderPage title="Impuestos" /> },
      ]},
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export default router;
